import _ from 'lodash';
import { connect } from 'react-redux';
import Tooltip from '@mui/material/Tooltip';
import DatePicker from 'react-datepicker';
import NumberFormat from 'react-number-format';
import { Divider, Button } from '@mui/material';
import React, { useState, useEffect } from 'react';
import ConfirmationModal from './ConfirmationModal';
import CustomSelectWithTooltip from './CustomSelectWithTooltip';
import { Actions as ADMINACTIONS } from '../../store/actions/adminActions';
import { Actions as USAGEACTIONS } from '../../store/actions/usageActions';
import { getUsageOptionsByFilteredCF, getFactorYear } from '../UsageData/utils/UsageUtils';
import { addYears, differenceInDays, formatISO, isLeapYear } from 'date-fns';
import { AddButton, CalendarBtn, AddUsageInputStyles, TooltipText } from '../../styledComponents/GlobalStyle';
import { parseDateToTz, isValidDateFormat, RemoveLeadingZeroes } from '../utils/GlobalFunctions';
import { Waveform } from '@uiball/loaders';
import { NoPermissions, BetaTag } from '../Common/ReusableComponents/Reusable';

export const itemToOptions = (item) => ({
  value: item?.id,
  label: item?.name || item?.priceType,
  description: item?.description,
  // for sub entities we need the metric and calc_market property
  metric: item?.metric || null,
  fuel_mix: item?.calc_market || null,
});

const AddEditUsage = ({
  item,
  metric,
  prevObj,
  options,
  currency,
  branding,
  prevDates,
  showLoading,
  gridSources,
  adminPortal,
  cyfDataAdmin,
  getUsageById,
  usageDetails,
  orderPriority,
  saveUsageData,
  solarConsumed,
  editUsageModal,
  waterTreatment,
  usageOptionsOK,
  getUsageOptions,
  usageTypeDetails,
  solarSubentities,
  getAllItemsFromDB,
  conversionFactors,
  getFilteredFactors,
  showUsageDataModal,
  electricitySources,
  organisationCurrency,
  suppliersEmissionsFactor,
  useSupplierCarbonFactor,
  metrics,
  seeDetails,
  font,
  factors,
  factorDetails,
  feature,
  aprilFools,
  hasAprilFools,
  user,
}) => {
  // Local States
  const [showMore, setShowMore] = useState(false);
  const [showFactorDetails, setShowFactorDetails] = useState(false);
  const [showFuelImport, setShowFuelImport] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [invalidUsageDate, setInvalidUsageDate] = useState(false);
  const [hasFuelMix, setHasFuelMix] = useState(item?.fuel_mix ? true : false);
  const [localGridMix, setLocalGridMix] = useState(item?.fuel_mix || { 599324: '100' });

  // Supplier factor is now handled in its own state.
  const [hasSupplierFactor, setHasSupplierFactor] = useState(item?.useSupplierCarbonFactor || 'No');
  const [supplierFactor, setSupplierFactor] = useState(item?.suppliersEmissionsFactor ?? null);

  const [showPreviousDiv, setShowPreviousDiv] = useState(false);
  const [localTextState, setLocalTextState] = useState({
    userLabel: null,
    usage: null,
    waterTreatment: null,
    solarConsumed: null,
    suppliersEmissionsFactor: null,
  });
  const [isLocationVisible, setIsLocationVisible] = useState(true);
  const [isMarketVisible, setIsMarketVisible] = useState(true);

  const toggleLocationVisibility = () => setIsLocationVisible(!isLocationVisible);
  const toggleMarketVisibility = () => setIsMarketVisible(!isMarketVisible);

  const { primaryColor, tooltipBackground, tooltipText } = branding || {};

  const localGridChange = (value, name) => {
    if (!hasFuelMix) {
      setLocalGridMix({ 599324: '100' });
    } else {
      const prevMix = { ...localGridMix };
      prevMix[name] = parseFloat(value) <= 0 ? '0' : RemoveLeadingZeroes(value);
      // Delete the default residual key if multiple values exist
      if (Object.keys(prevMix).length > 1) delete prevMix['599324'];
      setLocalGridMix(prevMix);
    }
  };

  const fuelMixSum = Number(
    Object.values(localGridMix)
      .reduce((a, b) => Number(a) + Number(b), 0)
      .toFixed(11)
  );

  const showButton =
    item?.dateEnd &&
    item?.facility &&
    usageOptionsOK &&
    item?.subentity &&
    item?.dateStart &&
    item?.metric &&
    !invalidUsageDate &&
    !!conversionFactors?.length > 0 &&
    (showFuelImport ? fuelMixSum === 100 : true) &&
    new Date(item?.dateStart) <= new Date(item?.dateEnd);

  //is a facility, subentity, usage>0, usageOptions set, and gridmix adding up to 100, if there is one?
  const canSubmitUsage =
    item?.dateEnd &&
    item?.facility &&
    usageOptionsOK &&
    item?.subentity &&
    item?.dateStart &&
    item?.metric &&
    !invalidUsageDate &&
    !!conversionFactors?.length &&
    Number(localTextState?.usage) > 0 &&
    (showFuelImport ? fuelMixSum === 100 || (fuelMixSum === 0 && localTextState?.suppliersEmissionsFactor != null) : true) &&
    new Date(item?.dateStart) <= new Date(item?.dateEnd);

  useEffect(() => {
    if (options?.subentity?.value?.value && item?.dateEnd) {
      const subentityId = options.subentity.value.value;
      const subentityPossibleFuelMix = options.subentity.value.fuel_mix || false;
      const metric = options.metric?.value?.value;
      if (metric) getFilteredFactors(formatISO(new Date(item.dateEnd)), subentityId, subentityPossibleFuelMix, metric);
    }
  }, [
    adminPortal,
    item?.dateEnd,
    item?.dateStart,
    item?.organisation,
    getFilteredFactors,
    options?.metric?.value?.value,
    options?.subentity?.value?.value,
    options?.subentity?.value?.fuel_mix,
  ]);

  useEffect(() => {
    if (item?.dateStart && item?.dateEnd) {
      const dateDiffInDays = differenceInDays(new Date(item?.dateEnd), new Date(item?.dateStart));
      const usageStartYear = new Date(item?.dateStart).getFullYear();
      const checkForEndYear = formatISO(new Date(item?.dateStart)) >= formatISO(new Date(usageStartYear, 2, 1));
      const leapYear = checkForEndYear
        ? isLeapYear(addYears(new Date(item?.dateStart), 1))
        : isLeapYear(new Date(item?.dateStart));
      const maxUsageSpanDays = leapYear ? 365 : 364;

      setInvalidUsageDate(dateDiffInDays > maxUsageSpanDays);
    }
  }, [item?.dateStart, item?.dateEnd]);

  useEffect(() => setHasFuelMix(gridSources?.length > 0), [gridSources?.length]);

  useEffect(() => {
    if (item?.id || item?.copy) {
      setLocalTextState((state) => ({
        ...state,
        userLabel: localTextState?.userLabel || item?.userLabel || null,
        rawUsage: localTextState?.usage || item?.copy ? null : item?.rawUsage || null,
        usage: item?.copy ? '' : item?.rawUsage || item?.usage || '',
        suppliersEmissionsFactor: item?.suppliersEmissionsFactor || null,
      }));
      if (item?.fuel_mix) setLocalGridMix(item?.fuel_mix);
      // For edit mode, if the usage has fuel mix different Residual show the grid mix inputs
      if (item?.suppliersEmissionsFactor || (item?.fuel_mix && item?.fuel_mix?.['599324'] !== '100'))
        setShowFuelImport(true);
    }
  }, [item]);

  // Clear usage fields when entity or subentity changes.
  useEffect(() => {
    setLocalTextState((state) => ({
      ...state,
      rawUsage: null,
      usage: '',
    }));
  }, [item?.entity, item?.subentity]);

  useEffect(() => {
    if (metric?.isSpend && !item?.currency && organisationCurrency) {
      if (item?.id || item?.copy) {
        editUsageModal(item?.rawUsageCurrency || metric?.currencyId || organisationCurrency.id, { name: 'currency' });
      } else {
        editUsageModal(organisationCurrency?.id, { name: 'currency' });
      }
      editUsageModal(true, { name: 'isSpend' });
    }
  });

  useEffect(() => {
    const facilities = options?.facility?.values;
    if (facilities?.length === 1 && !options?.facility?.value) editUsageModal(facilities[0]?.value, { name: 'facility' });
  }, []);

  const betaTag = feature && feature?.isBeta === true && (
    <BetaTag color={branding?.btnTextColor} backgroundColor={branding?.primaryButton} />
  );

  useEffect(() => {
    if (prevObj && JSON.stringify(prevObj) !== JSON.stringify(item)) {
      setShowPreviousDiv(true);
    } else {
      setShowPreviousDiv(false);
    }
  }, [prevObj]);

  const [buttonPosition, setButtonPosition] = useState({ top: '95%', left: '50%' });
  const handleMouseOver = () => {
    const randomTop = Math.random() * 90 + '%';
    const randomLeft = Math.random() * 90 + '%';
    setButtonPosition({ top: randomTop, left: randomLeft });
  };

  return (
    <div className='modal' style={{ display: 'block', background: 'rgba(0,0,0,.5)' }}>
      <div className='modal-dialog' style={{ height: '85vh', fontSize: '12px' }}>
        <div className='modal-content' style={{ background: 'transparent' }}>
          <div className='modal-header' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 className='modal-title'>{item?.id ? 'Edit' : 'Add'} Usage Data</h4>
            <button onClick={() => showUsageDataModal(false, null)} type='button' className='modal-close'>
              <i className='fa fa-times' />
            </button>
          </div>
          <div
            id='modal-body'
            className='modal-body'
            style={{ overflow: 'auto', background: 'transparent', height: '85vh', padding: 0 }}>
            <div
              style={{
                padding: 15,
                overflow: 'visible',
                background: 'white',
                position: 'relative',
                borderBottomLeftRadius: '6px',
                borderBottomRightRadius: '6px',
              }}>
              {showLoading && (
                <div
                  style={{
                    top: '0',
                    left: '0',
                    zIndex: '999',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    position: 'absolute',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <Waveform size={40} lineWeight={3.5} speed={1} color='black' />
                </div>
              )}
              {showPreviousDiv && (
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    paddingBottom: '10px',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}>
                  <h4 style={{ marginTop: '0' }}>
                    Data Successfully Saved! <i className='fa-solid fa-circle-check' style={{ color: 'green' }}></i>
                  </h4>
                  <hr style={{ color: '#ccc', width: '100%', marginBottom: 0, marginTop: 0 }} />
                </div>
              )}
              <div
                style={{
                  gap: '15px',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  transition: 'filter 0.3s ease-in-out',
                  filter: showLoading ? 'blur(1.5px)' : 'none',
                }}>
                {/* Facility */}
                <div style={{ gridColumn: 'span 2 / span 2' }}>
                  <label>
                    Facility <em>*</em>
                  </label>
                  <CustomSelectWithTooltip
                    name='facility'
                    isClearable={true}
                    options={options?.facility?.values}
                    value={options?.facility?.value || null}
                    onChange={(s, e) => editUsageModal(s?.value, e)}
                  />
                </div>

                {/* Start Date */}
                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Start date *
                    {prevDates?.dateStart && (
                      <button
                        style={{
                          border: 0,
                          color: '#1f9b37',
                          paddingRight: '0',
                          background: 'inherit',
                          textDecoration: 'underline',
                        }}
                        onClick={() => editUsageModal(prevDates, { name: 'prevDates' })}>
                        Use Previous Dates
                      </button>
                    )}
                  </label>
                  <label className='input-group date datepicker'>
                    <DatePicker
                      selectsStart
                      showYearDropdown
                      showMonthDropdown
                      isClearable={true}
                      maxDate={new Date()}
                      id='UsageData-startDate'
                      scrollableYearDropdown
                      dateFormat='dd/MM/yyyy'
                      yearDropdownItemNumber={5}
                      popperPlacement='bottom-end'
                      className='form-control startDate'
                      endDate={item?.dateEnd && new Date(item?.dateEnd)}
                      startDate={item?.dateStart && new Date(item?.dateStart)}
                      selected={(item?.dateStart && parseDateToTz(item?.dateStart, 'yyyy-MM-dd')) || null}
                      onChange={(date, e) => {
                        const dateInput = e?.target?.value;
                        if (!date) {
                          editUsageModal(date, { name: 'dateStart' });
                        } else if (dateInput && !isValidDateFormat(dateInput)) {
                          console.log('Invalid date format');
                        } else {
                          editUsageModal(formatISO(date), { name: 'dateStart' });
                          if (!item?.dateEnd || new Date(item?.dateEnd) < date) {
                            editUsageModal(formatISO(date), { name: 'dateEnd' });
                          }
                        }
                      }}
                    />
                    <span className='input-group-btn'>
                      <CalendarBtn className='btn btn-primary date-set'>
                        <i className='fa fa-calendar' />
                      </CalendarBtn>
                    </span>
                  </label>
                </div>

                {/* End Date */}
                <div>
                  <label>End date *</label>
                  <label className='input-group date datepicker'>
                    <DatePicker
                      selectsEnd
                      showYearDropdown
                      showMonthDropdown
                      isClearable={true}
                      scrollableYearDropdown
                      id='UsageData-endDate'
                      dateFormat='dd/MM/yyyy'
                      yearDropdownItemNumber={5}
                      popperPlacement='bottom-end'
                      className='form-control endDate'
                      endDate={item?.dateEnd && new Date(item?.dateEnd)}
                      minDate={item?.dateStart && new Date(item?.dateStart)}
                      startDate={item?.dateStart && new Date(item?.dateStart)}
                      onChange={(date, event) => {
                        const inputDate = event?.target?.value;
                        if (!date && !inputDate) {
                          editUsageModal(null, { name: 'dateEnd' });
                        } else if (inputDate && isValidDateFormat(inputDate)) {
                          const parsedDate = parseDateToTz(inputDate, 'dd/MM/yyyy');
                          editUsageModal(formatISO(parsedDate), { name: 'dateEnd' });
                        } else if (date) {
                          editUsageModal(formatISO(date), { name: 'dateEnd' });
                        }
                      }}
                      selected={item?.dateEnd ? new Date(item.dateEnd) : null}
                    />
                    <span className='input-group-btn'>
                      <CalendarBtn className='btn btn-primary date-set'>
                        <i className='fa fa-calendar' />
                      </CalendarBtn>
                    </span>
                  </label>
                </div>

                {/* Invalid Date Message */}
                {invalidUsageDate && (
                  <div style={{ gridColumn: 'span 2 / span 2', margin: 'auto' }}>
                    <label style={{ color: 'red', fontWeight: 'bold' }}>
                      <i
                        className='fa-solid fa-triangle-exclamation'
                        style={{ fontSize: 20, marginRight: 10, verticalAlign: 'sub' }}
                      />
                      Your date span cannot exceed 1 year.
                    </label>
                  </div>
                )}

                {/* Custom Top-Level Category */}
                <div style={{ gridColumn: 'span 2 / span 2' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Custom top-level category
                    <Tooltip
                      title={<TooltipText>Optional customised top-level category name.</TooltipText>}
                      placement='right'
                      arrow
                      componentsProps={{
                        tooltip: { sx: { backgroundColor: tooltipBackground, color: tooltipText } },
                        arrow: { sx: { color: tooltipBackground } },
                      }}>
                      <i
                        className='fa fa-question-circle'
                        style={{ cursor: 'pointer', marginLeft: '0.5rem', color: primaryColor }}
                      />
                    </Tooltip>
                  </label>
                  <input
                    name='userLabel'
                    id='UsageData-userLabel'
                    style={AddUsageInputStyles}
                    value={localTextState?.userLabel || ''}
                    onChange={(e) => setLocalTextState((state) => ({ ...state, userLabel: e?.target?.value || null }))}
                  />
                </div>

                {/* Usage Type */}
                <div style={{ gridColumn: 'span 2 / span 2' }}>
                  <label>
                    Usage type <em>*</em>
                  </label>
                  <CustomSelectWithTooltip
                    zIndex='auto'
                    name='entity'
                    isClearable={true}
                    options={options?.entity?.values}
                    value={options?.entity?.value || null}
                    onChange={(s, e) => {
                      editUsageModal(s?.value, e);
                      getUsageOptions(s?.value);
                    }}
                  />
                </div>

                {/* Dynamic Usage Options Fields */}
                {item?.dateStart &&
                  _.sortBy(Object.entries(options), ([key]) => orderPriority[key] || Infinity)
                    .filter(
                      ([key]) =>
                        options[key]?.values?.length > 0 &&
                        !['facility', 'entity'].includes(key) &&
                        !(key === 'metric' && options?.metric?.values?.length === 1)
                    )
                    .map(([key, option]) => (
                      <div key={key} style={{ gridColumn: 'span 2 / span 2' }}>
                        <label>
                          {option.title} <em>*</em>
                        </label>
                        <CustomSelectWithTooltip
                          name={key}
                          item={item}
                          zIndex='auto'
                          isClearable={true}
                          options={option.values}
                          value={option.value || null}
                          editUsageModal={editUsageModal}
                          usageTypeDetails={usageTypeDetails}
                          onChange={(s, e) => editUsageModal(key === 'subentity' ? s : s?.value, e)}
                        />
                      </div>
                    ))}

                {/* Usage Number */}
                {item?.dateStart && item?.metric && (
                  <div style={{ gridColumn: 'span 2 / span 2' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center' }}>
                      Usage in&nbsp;
                      {metric?.isSpend ? `${currency?.symbol} (${currency?.isoCode})` : options.metric?.value?.label}
                      &nbsp;
                      <em>*</em>
                      <Tooltip
                        title={
                          <TooltipText>
                            {metric?.isSpend
                              ? `Amount of ${currency?.symbol} (${currency?.isoCode}) you spent on usage type.`
                              : options?.metric?.value?.description || ''}
                          </TooltipText>
                        }
                        placement='right'
                        arrow
                        componentsProps={{
                          tooltip: { sx: { backgroundColor: tooltipBackground, color: tooltipText } },
                          arrow: { sx: { color: tooltipBackground } },
                        }}>
                        <i
                          className='fa fa-question-circle'
                          style={{ cursor: 'pointer', marginLeft: '0.5rem', color: primaryColor }}
                        />
                      </Tooltip>
                    </label>
                    <div className='input-container' style={{ position: 'relative' }}>
                      <NumberFormat
                        name={'numberName'}
                        id='UsageData-usage'
                        inputMode='numeric'
                        allowNegative={false}
                        className='form-control'
                        value={localTextState.usage}
                        onValueChange={(e) => setLocalTextState((state) => ({ ...state, usage: e.floatValue || null }))}
                        isAllowed={(values) => {
                          const { floatValue } = values;
                          return floatValue === undefined || floatValue < 1000000000000;
                        }}
                      />
                      <span
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          opacity: 0.4,
                          pointerEvents: 'none',
                        }}>
                        {metric?.isSpend ? `${currency?.symbol}` : options.metric?.value?.label}
                      </span>
                    </div>
                  </div>
                )}

                {/* Water / Solar Usage */}
                {(waterTreatment || solarConsumed) && (
                  <div style={{ gridColumn: 'span 2 / span 2' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center' }}>
                      {waterTreatment ? 'Water Treatment in' : 'Solar Consumed in'} {options?.metric?.value?.label}
                      &nbsp;
                      <em>*</em>
                      <Tooltip
                        title={<TooltipText>{options?.metric?.value?.description || ''}</TooltipText>}
                        placement='right'
                        arrow
                        componentsProps={{
                          tooltip: { sx: { backgroundColor: tooltipBackground, color: tooltipText } },
                          arrow: { sx: { color: tooltipBackground } },
                        }}>
                        <i
                          className='fa fa-question-circle'
                          style={{ cursor: 'pointer', marginLeft: '0.5rem', color: primaryColor }}
                        />
                      </Tooltip>
                    </label>
                    <NumberFormat
                      inputMode='numeric'
                      allowNegative={false}
                      className='form-control'
                      name={waterTreatment ? 'waterTreatment' : 'solarConsumed'}
                      value={localTextState[waterTreatment ? 'waterTreatment' : 'solarConsumed']}
                      onValueChange={(values) =>
                        setLocalTextState((state) => ({
                          ...state,
                          [waterTreatment ? 'waterTreatment' : 'solarConsumed']: values.floatValue ?? null,
                        }))
                      }
                    />
                  </div>
                )}

                {/* Energy Fuel Mix */}
                {item?.dateStart && item?.entity && item?.subentity && options?.subentity?.value?.fuel_mix && (
                  <>
                    {/* Grid Toggle */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gridColumn: 'span 2 / span 2',
                        justifyContent: 'space-between',
                      }}>
                      <label style={{ display: 'inline-flex', marginBottom: '0', alignItems: 'center' }}>
                        Do you have your supplier's emission factor{gridSources?.length > 0 ? ' or fuel mix' : ''}?
                        <Tooltip
                          title={
                            <TooltipText>
                              {
                                'Please enter your electricity emissions factor and/or fuel mix. You can find this information on your energy provider’s website or in your utility bills. If entering a fuel mix, enter this as a percentage and ensure it totals to 100%. If entering an emissions factor, note that Scope 3 emissions will also be calculated automatically.'
                              }
                            </TooltipText>
                          }
                          placement='top'
                          arrow
                          componentsProps={{
                            tooltip: { sx: { backgroundColor: tooltipBackground, color: tooltipText } },
                            arrow: { sx: { color: tooltipBackground } },
                            popper: { sx: { zIndex: 99999 } },
                          }}>
                          <i
                            className='fa fa-question-circle'
                            style={{ cursor: 'pointer', marginLeft: '0.5rem', color: primaryColor }}
                          />
                        </Tooltip>
                      </label>
                      <div style={{ gap: '1rem', display: 'flex' }}>
                        <button
                          disabled={showFuelImport}
                          className='btn btn-success'
                          id='UsageData-fuelMix-yes'
                          onClick={() => {
                            if (gridSources?.length > 0) {
                              setLocalGridMix(Object.fromEntries(gridSources.map((str) => [str, '0'])));
                            }
                            setShowFuelImport(true);
                            setLocalTextState((state) => ({ ...state, suppliersEmissionsFactor: null }));
                          }}>
                          Yes
                        </button>
                        <button
                          disabled={!showFuelImport}
                          className='btn btn-warning'
                          id='UsageData-fuelMix-no'
                          onClick={() => {
                            setLocalGridMix({ 599324: '100' });
                            setShowFuelImport(false);
                          }}>
                          No
                        </button>
                      </div>
                    </div>

                    {/* Supplier Factor */}
                    {showFuelImport && (
                      <div style={{ gridColumn: 'span 2 / span 2' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center' }}>
                          Supplier Factor (Scope 2 Only)
                          <Tooltip
                            title={
                              <TooltipText>
                                {
                                  "You will find your energy providers' emission factor via their website or in your utility bills. Enter the value of the emissions factor in kgCO2e / kWh."
                                }
                              </TooltipText>
                            }
                            placement='right'
                            arrow
                            componentsProps={{
                              tooltip: { sx: { backgroundColor: tooltipBackground, color: tooltipText } },
                              arrow: { sx: { color: tooltipBackground } },
                            }}>
                            <i
                              className='fa fa-question-circle'
                              style={{ cursor: 'pointer', marginLeft: '0.5rem', color: primaryColor }}
                            />
                          </Tooltip>
                        </label>
                        <div className='input-container' style={{ position: 'relative' }}>
                          <NumberFormat
                            name={'suppliersEmissionsFactor'}
                            id='UsageData-suppliersEmissionsFactor'
                            inputMode='numeric'
                            allowNegative={false}
                            className='form-control'
                            value={localTextState.suppliersEmissionsFactor}
                            onValueChange={(values) =>
                              setLocalTextState((state) => ({
                                ...state,
                                suppliersEmissionsFactor: values.floatValue || null,
                              }))
                            }
                            isAllowed={(values) => {
                              const { floatValue } = values;
                              return floatValue === undefined || floatValue < 1000000000000;
                            }}
                          />
                          <span
                            style={{
                              position: 'absolute',
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              opacity: 0.4,
                              pointerEvents: 'none',
                            }}>
                            kgCO2e/kWh
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Grid Mix for other sources */}
                    {showFuelImport &&
                      gridSources?.length > 0 &&
                      gridSources
                        .filter((gs) => gs !== '599324')
                        .map((gs, index) => (
                          <div
                            key={index}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <label style={{ maxWidth: '50%' }}>{_.find(electricitySources, { id: gs })?.source}</label>
                            <NumberFormat
                              name={gs}
                              defaultValue={0}
                              inputMode='numeric'
                              allowNegative={false}
                              style={{ width: '50%' }}
                              value={localGridMix?.[gs]}
                              isAllowed={(values) => {
                                if (!values.value) return true;
                                const { floatValue } = values;
                                return floatValue < 101;
                              }}
                              className='form-control'
                              id={`UsageData-fuelMix-${gs}`}
                              onChange={(e) => localGridChange(e.target.value || 0, gs)}
                            />
                          </div>
                        ))}

                    {showFuelImport &&
                      localGridMix?.['599324'] !== '100' &&
                      ((fuelMixSum !== 100 && fuelMixSum !== 0 && localTextState?.suppliersEmissionsFactor != null) ||
                        (fuelMixSum !== 100 && localTextState?.suppliersEmissionsFactor == null)) && (
                        <div style={{ gridColumn: 'span 2 / span 2', margin: 'auto' }}>
                          <label style={{ color: 'red', verticalAlign: 'sub', fontWeight: 'bold' }}>
                            <i className='fa-solid fa-triangle-exclamation' style={{ marginRight: 10, fontSize: 20 }} />
                            Grid mix does not make up 100% or missing the supplier factor.
                          </label>
                        </div>
                      )}
                  </>
                )}

                {/* Editing Solar Usage */}
                {item?.id && solarSubentities?.includes(item?.subentity) && (
                  <div
                    className='alert alert-info'
                    style={{ color: '#130384', marginBottom: '0', gridColumn: 'span 2 / span 2' }}>
                    <i className='fa fa-exclamation-triangle' aria-hidden='true' />
                    &nbsp; When editing Solar usage, ensure that your corresponding data for the same time periods fits this
                    formula:
                    <p style={{ margin: 'auto', width: 'fit-content' }}>
                      Solar Generated kWh = Solar Consumed kWh + Solar Exported kWh
                    </p>
                  </div>
                )}

                {/* No Carbon Factor Message */}
                {item?.dateStart && conversionFactors?.length === 0 && (
                  <div style={{ gridColumn: 'span 2 / span 2', margin: 'auto' }}>
                    <label style={{ color: 'red', fontWeight: 'bold' }}>
                      <i
                        className='fa-solid fa-triangle-exclamation'
                        style={{ fontSize: 20, marginRight: 10, verticalAlign: 'sub' }}
                      />
                      Sorry, we don't have a carbon factor for that year, please contact support for help.
                    </label>
                  </div>
                )}

                {/* Submit Button */}
                {parseFloat(localTextState?.usage) > 0 && hasAprilFools && aprilFools?.users?.includes(user) ? (
                  <div style={{ gridColumn: 'span 2 / span 2', margin: 'auto', marginBottom: '32px' }}>
                    <AddButton
                      id='submit-usage'
                      style={{
                        position: 'absolute',
                        top: buttonPosition.top,
                        left: buttonPosition.left,
                        transform: 'translate(-50%, -50%)',
                        zIndex: '999',
                      }}
                      onMouseOver={handleMouseOver}
                      className={`btn btn-${canSubmitUsage ? 'success' : 'warning'}`}
                      onClick={() => {
                        if (canSubmitUsage) {
                          saveUsageData(
                            [
                              {
                                ...item,
                                usage: localTextState?.usage,
                                waterTreatment: localTextState?.waterTreatment,
                                solarConsumed: localTextState?.solarConsumed,
                                userLabel: localTextState?.userLabel,
                                fuel_mix: (hasFuelMix && localGridMix) || null,
                                suppliersEmissionsFactor: supplierFactor,
                                useSupplierCarbonFactor: supplierFactor !== null ? 'Yes' : 'No',
                              },
                            ],
                            adminPortal
                          );
                          setHasFuelMix(false);
                          setLocalGridMix(false);
                          setLocalTextState({
                            usage: null,
                            userLabel: null,
                            waterTreatment: null,
                            solarConsumed: null,
                          });
                          setSupplierFactor(null);
                          setShowConfirmModal(true);
                        }
                      }}>
                      {canSubmitUsage ? `${item?.id ? 'Edit' : 'Add'} Data` : 'Data Incomplete'}
                    </AddButton>
                  </div>
                ) : (
                  <div style={{ gridColumn: 'span 2 / span 2', margin: 'auto' }}>
                    <AddButton
                      id='submit-usage'
                      style={{ cursor: !canSubmitUsage && 'not-allowed' }}
                      className={`btn btn-${canSubmitUsage ? 'success' : 'warning'}`}
                      onClick={() => {
                        if (canSubmitUsage) {
                          saveUsageData(
                            [
                              {
                                ...item,
                                usage: localTextState?.usage,
                                waterTreatment: localTextState?.waterTreatment,
                                solarConsumed: localTextState?.solarConsumed,
                                userLabel: localTextState?.userLabel,
                                fuel_mix: hasFuelMix && fuelMixSum === 100 ? localGridMix : { 599324: '100' },
                                suppliersEmissionsFactor: localTextState?.suppliersEmissionsFactor || null,
                              },
                            ],
                            adminPortal
                          );
                          setHasFuelMix(false);
                          setLocalGridMix(false);
                          setLocalTextState({
                            usage: null,
                            userLabel: null,
                            waterTreatment: null,
                            solarConsumed: null,
                            suppliersEmissionsFactor: null,
                          });
                          setSupplierFactor(null);
                          setShowConfirmModal(true);
                        }
                      }}>
                      {canSubmitUsage ? `${item?.id ? 'Edit' : 'Add'} Data` : 'Data Incomplete'}
                    </AddButton>
                  </div>
                )}

                {!item?.id && showButton && factorDetails && (
                  <>
                    <div style={{ gridColumn: 'span 2 / span 2' }}>
                      <Divider color='#AAA' />
                    </div>
                    <div
                      style={{
                        gridColumn: 'span 2 / span 2',
                        margin: 'auto',
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-evenly',
                      }}>
                      <Button
                        variant='outlined'
                        disableRipple
                        disableFocusRipple
                        onClick={() => setShowFactorDetails(!showFactorDetails)}
                        sx={{
                          border: `2px solid ${branding?.primaryButton}`,
                          fontSize: '14px',
                          fontWeight: '600',
                          borderRadius: '6px',
                          padding: '5px',
                          textTransform: 'none',
                          backgroundColor: '#F9F9F9',
                          color: '	#1A1A1A',
                          fontFamily: font,
                          transition: 'all 0.2s ease-out',
                          '&:hover': {
                            border: `2px solid ${branding?.buttonHover}`,
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                          },
                        }}>
                        {showFactorDetails ? 'Hide' : 'See Emissions Factor Details'}
                        {betaTag}
                      </Button>
                    </div>
                  </>
                )}

                {/* Admin and More Details */}
                {item?.id && showButton && (
                  <>
                    <div
                      style={{ gridColumn: 'span 2 / span 2', display: factorDetails || cyfDataAdmin ? 'block' : 'none' }}>
                      <Divider color='#AAA' />
                    </div>
                    <div
                      style={{
                        gridColumn: 'span 2 / span 2',
                        margin: 'auto',
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-evenly',
                      }}>
                      <div style={{ display: cyfDataAdmin ? 'flex' : 'none' }}>
                        <Button
                          variant='outlined'
                          disableRipple
                          disableFocusRipple
                          onClick={() => {
                            if (!usageDetails) getUsageById(item?.id);
                            setShowMore(!showMore);
                            if (!showMore === true) setShowFactorDetails(false);
                          }}
                          sx={{
                            border: `2px solid ${branding?.primaryButton}`,
                            fontSize: '14px',
                            fontWeight: '600',
                            borderRadius: '6px',
                            padding: '5px',
                            textTransform: 'none',
                            backgroundColor: '#F9F9F9',
                            color: '	#1A1A1A',
                            fontFamily: font,
                            transition: 'all 0.2s ease-out',
                            '&:hover': {
                              border: `2px solid ${branding?.buttonHover}`,
                              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                            },
                          }}>
                          {showMore ? 'Hide' : 'See More'}
                        </Button>
                      </div>
                      <div style={{ display: factorDetails ? 'flex' : 'none' }}>
                        <Button
                          variant='outlined'
                          disableRipple
                          disableFocusRipple
                          onClick={() => {
                            if (!usageDetails) getUsageById(item?.id);
                            setShowFactorDetails(!showFactorDetails);
                            if (!showFactorDetails === true) setShowMore(false);
                          }}
                          sx={{
                            border: `2px solid ${branding?.primaryButton}`,
                            fontSize: '14px',
                            fontWeight: '600',
                            borderRadius: '6px',
                            padding: '5px',
                            textTransform: 'none',
                            backgroundColor: '#F9F9F9',
                            color: '	#1A1A1A',
                            fontFamily: font,
                            transition: 'all 0.2s ease-out',
                            '&:hover': {
                              border: `2px solid ${branding?.buttonHover}`,
                              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                            },
                          }}>
                          {showFactorDetails ? 'Hide' : 'See Emissions Factor Details'}
                          {betaTag}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
                {showMore && usageDetails && cyfDataAdmin && item?.id && (
                  <div style={{ overflowWrap: 'break-word', gridColumn: 'span 2 / span 2' }}>
                    <table>
                      <tbody>
                        <tr key='modifiedDate'>
                          <td>
                            <p>
                              <b>Modified Date:</b> {usageDetails?.modifiedDate}
                            </p>
                            <p>
                              <b>Usage Data End Date:</b> {usageDetails?.dateEnd}
                            </p>
                          </td>
                        </tr>
                        {['location', 'market'].map((el, key) => (
                          <>
                            <tr key={key}>
                              <td>
                                <p style={{ fontWeight: 'bold' }}>
                                  {el.charAt(0).toUpperCase() + el.slice(1)}:
                                  <button
                                    onClick={() => {
                                      el === 'location' ? toggleLocationVisibility() : toggleMarketVisibility();
                                    }}
                                    style={{ marginLeft: 5, border: 0, backgroundColor: 'inherit' }}>
                                    {el === 'location' ? (isLocationVisible ? '▼' : '▶') : isMarketVisible ? '▼' : '▶'}
                                  </button>
                                </p>
                              </td>
                            </tr>
                            {(el === 'location' ? isLocationVisible : isMarketVisible) &&
                              ['calendarYear', 'reportingYear'].map((elem, index) => (
                                <>
                                  <tr key={index}>
                                    <td>
                                      <p style={{ fontWeight: 'bold' }}>{elem.charAt(0).toUpperCase() + elem.slice(1)}</p>
                                    </td>
                                  </tr>
                                  <tr key={`object-${index}`}>
                                    <td>
                                      {Object.keys(usageDetails?.[el]?.[elem] || {}).map((x, i) =>
                                        x !== 'conversionFactors' ? (
                                          <ol
                                            key={i}
                                            style={{
                                              display: 'flex',
                                              flexWrap: 'wrap',
                                              paddingLeft: 20,
                                              marginBottom: 5,
                                            }}>
                                            <p style={{ marginRight: 5 }}>
                                              <b>{x === 'year' ? 'Ideal Factor Year' : x === 'endDate' ? 'RYE' : x}</b>:
                                            </p>{' '}
                                            {typeof usageDetails?.[el]?.[elem]?.[x] === 'object' &&
                                            usageDetails?.[el]?.[elem]?.[x] !== null ? (
                                              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                                {Object.entries(usageDetails?.[el]?.[elem]?.[x] || {}).map(
                                                  ([key, value]) => (
                                                    <div key={key} style={{ marginRight: '10px' }}>
                                                      <strong>{key}:</strong> {JSON.stringify(value)}
                                                    </div>
                                                  )
                                                )}
                                              </div>
                                            ) : (
                                              JSON.stringify(usageDetails?.[el]?.[elem]?.[x])
                                            )}
                                          </ol>
                                        ) : (
                                          Object.entries(usageDetails?.[el]?.[elem]?.[x]?.[0] || {}).map(
                                            ([key, value]) =>
                                              key === 'year' && (
                                                <ol
                                                  key={key}
                                                  style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    paddingLeft: 20,
                                                    marginBottom: 5,
                                                  }}>
                                                  <p style={{ marginRight: 5 }}>
                                                    <strong>Chosen Factor Year:</strong> {value}
                                                  </p>
                                                </ol>
                                              )
                                          )
                                        )
                                      )}
                                    </td>
                                  </tr>
                                </>
                              ))}
                          </>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {showFactorDetails && usageDetails && factorDetails && item?.id && showButton && (
                  <div
                    style={{
                      display: 'flex',
                      padding: 10,
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gridColumn: 'auto / span 2',
                      width: '95%',
                      justifySelf: 'center',
                    }}>
                    <div>
                      <h4 style={{ display: 'flex', marginTop: 5, justifyContent: 'center', fontWeight: 'bold' }}>
                        Emissions Factor Details:
                      </h4>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        overflowWrap: 'break-word',
                        gridColumn: 'span 2 / span 2',
                        justifyContent: 'center',
                        flexDirection: 'column',
                      }}>
                      {factors?.map((factor, index) => (
                        <div
                          key={`factor-${index}`}
                          style={{
                            display: 'flex',
                            border: '1px solid #CCC',
                            borderRadius: '15px',
                            padding: 10,
                            justifyContent: 'center',
                            flexDirection: 'column',
                            marginBottom: 10,
                          }}>
                          <table className='table table-hover general-table' style={{ width: '100%' }}>
                            <tbody>
                              {Array.from(seeDetails.keys())
                                .filter((d) => d !== 'Metric')
                                .map((detail, key) => {
                                  const value = seeDetails.get(detail)(index);
                                  return (
                                    <tr key={key} style={{ display: value ? 'table-row' : 'none' }}>
                                      <td style={{ fontWeight: 'bold', borderTop: key === 0 ? 0 : '1px solid #DDD' }}>
                                        {detail}:
                                      </td>
                                      <td style={{ borderTop: key === 0 ? 0 : '1px solid #DDD' }}>{value}</td>
                                    </tr>
                                  );
                                })}
                              <tr key={`emissionsFactors-${index}`}>
                                <td colSpan='2' style={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                                  Emissions Factors
                                </td>
                              </tr>
                              <tr key={`Scope-${index}`}>
                                <td colSpan='2'>
                                  <span>
                                    <b>Scope {factor?.scope}:</b> {factor?.co2e} kg CO2e / {seeDetails.get('Metric')(index)}
                                  </span>
                                </td>
                              </tr>
                              <tr key={`Scope3WTT-${index}`}>
                                <td colSpan='2' style={{ display: factor?.wtt ? 'table-cell' : 'none' }}>
                                  <span>
                                    <b>Scope 3 WTT:</b> {factor?.wtt} kg CO2e / {seeDetails.get('Metric')(index)}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {showFactorDetails && factorDetails && !item?.id && showButton && (
                  <div
                    style={{
                      display: 'flex',
                      padding: 10,
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gridColumn: 'auto / span 2',
                      width: '95%',
                      justifySelf: 'center',
                    }}>
                    <div>
                      <h4 style={{ display: 'flex', marginTop: 5, justifyContent: 'center', fontWeight: 'bold' }}>
                        Emissions Factor Details:
                      </h4>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        overflowWrap: 'break-word',
                        gridColumn: 'span 2 / span 2',
                        justifyContent: 'center',
                        flexDirection: 'column',
                      }}>
                      {factors?.map((factor, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            border: '1px solid #CCC',
                            borderRadius: '15px',
                            padding: 10,
                            justifyContent: 'center',
                            flexDirection: 'column',
                            marginBottom: 10,
                          }}>
                          <table className='table table-hover general-table' style={{ width: '100%' }}>
                            <tbody>
                              {Array.from(seeDetails.keys())
                                .filter((d) => d !== 'Metric')
                                .map((detail, key) => {
                                  const value = seeDetails.get(detail)(index);
                                  return (
                                    <tr key={key} style={{ display: value ? 'table-row' : 'none' }}>
                                      <td style={{ fontWeight: 'bold', borderTop: key === 0 ? 0 : '1px solid #DDD' }}>
                                        {detail}:
                                      </td>
                                      <td style={{ borderTop: key === 0 ? 0 : '1px solid #DDD' }}>{value}</td>
                                    </tr>
                                  );
                                })}
                              <tr key={`emissionsFactors-${index}`}>
                                <td colSpan='2' style={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                                  Emissions Factors
                                </td>
                              </tr>
                              <tr key={`Scope-${index}`}>
                                <td colSpan='2'>
                                  <span>
                                    <b>Scope {factor?.scope}:</b> {factor?.co2e} kg CO2e / {seeDetails.get('Metric')(index)}
                                  </span>
                                </td>
                              </tr>
                              <tr key={`Scope3WTT-${index}`}>
                                <td colSpan='2' style={{ display: factor?.wtt ? 'table-cell' : 'none' }}>
                                  <span>
                                    <b>Scope 3 WTT:</b> {factor?.wtt} kg CO2e / {seeDetails.get('Metric')(index)}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <ConfirmationModal showConfirmModal={showConfirmModal} setShowConfirmModal={setShowConfirmModal} />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const metrics = state?.metric?.list;
  const profile = state?.profile.details;
  const { item, adminPortal } = state?.usageDataModal || {};
  const usageTypeDetails = state?.admin?.adminPortal?.usageTypeDetails;
  const { entities, subentities, usageOptions, conversionFactors, filteredCF_UsageOpt } = state || {};
  const facilities = adminPortal
    ? _.filter(state?.admin?.adminPortal?.facilities, { organisationId: item.organisation })
    : state?.facility?.list;

  const currencies = state?.admin?.adminPortal?.currencies;
  const currency = currencies?.find((c) => c.id === item?.currency);
  const metric = metrics?.find((m) => m.id === item?.metric);
  const organisationCurrency = currencies?.find((c) => c.id === state?.organisation?.details?.currency);

  const solarSubentities = ['229591', '233875', '233408'];
  const solarConsumed = !item?.id && item?.metric === '4385' && item?.subentity === '229591';
  const waterTreatment = !item?.id && item?.metric === '4389' && item?.entity === '151938' && item?.subentity !== '185362';

  const orderPriority = {
    entity: 1,
    subentity: 2,
    metric: 3,
    finallocation: 4,
    originlocation: 5,
    factorprovider: 6,
    price: 7,
    currency: 8,
  };

  const options = {
    facility: {
      values: _.sortBy(facilities?.map(itemToOptions), ['label']),
      value: item?.facility && _.find(facilities?.map(itemToOptions), { value: item.facility }),
    },
    entity: {
      values: _.sortBy(entities?.map(itemToOptions), ['label']),
      value: item?.entity && _.find(entities?.map(itemToOptions), { value: item?.entity }),
    },
    subentity: {
      values: item?.entity
        ? _.sortBy(subentities?.filter((s) => s?.entity?.includes(item?.entity) && !s?.hideInUI)?.map(itemToOptions), [
            'label',
          ])
        : [],
      title: 'Usage Sub Type',
      value: item?.subentity && _.find(subentities?.map(itemToOptions), { value: item?.subentity }),
    },
  };

  options.metric = {
    values: item?.subentity
      ? _.sortBy(metrics?.filter((m) => options?.subentity?.value?.metric?.includes(m.id))?.map(itemToOptions), ['label'])
      : [],
    title: 'Usage In',
    value: item?.metric && _.find(metrics?.map(itemToOptions), { value: item?.metric }),
  };

  if ((organisationCurrency, options))
    if (usageOptions && item?.metric) {
      Object.keys(usageOptions).forEach((usageOpt, index, arr) => {
        const { displayName } = usageTypeDetails?.find((ut) => ut.type === usageOpt) || {};
        const label = displayName ? displayName : usageOpt.charAt(0).toUpperCase() + usageOpt.slice(1) + ' type';

        if (index === 0) {
          // Show immediately the first one
          options[usageOpt] = {
            values: _.sortBy(usageOptions[usageOpt]?.map(itemToOptions), ['label']),
            title: label,
            value: item?.[usageOpt] && _.find(usageOptions[usageOpt]?.map(itemToOptions), { value: item?.[usageOpt] }),
          };
          //Checks if the previous item is filled in, if it is we show the next option
          //Or if all the factors in filteredCf_UsageOpt dont contain the previous usageOpt we show the next one,
          //This is done to prevent the modal from not showing available usage options when the previous usage option is not filled in when it shouldn't be.
        } else if (filteredCF_UsageOpt?.every((opt) => opt[arr[index - 1]] == null) || item[arr[index - 1]]) {
          for (index; index <= arr.length - 1; index++) {
            // As soon as we select a usage option, we filter the CF that match that usage option and the above usage combo
            // Filter the usage options to just show the ones that match the filtered CF
            const filteredUsageOpt_CF = getUsageOptionsByFilteredCF(usageOptions, filteredCF_UsageOpt, item);
            options[usageOpt] = {
              values: _.sortBy(filteredUsageOpt_CF[usageOpt]?.map(itemToOptions), ['label']),
              title: label,
              value: item?.[usageOpt] && _.find(usageOptions[usageOpt]?.map(itemToOptions), { value: item?.[usageOpt] }),
            };
          }
        }
      });
    }

  options.currency = {
    values: metric?.isSpend //Only show the currency field if metric is Spend
      ? _.sortBy(
          currencies?.filter((x) => x.hideInUI !== true).map((x) => ({ label: `${x.symbol} (${x.isoCode})`, value: x.id })),
          ['label']
        )
      : [],
    title: 'Usage Data Input Currency',
    value:
      item?.currency &&
      _.find(
        currencies?.filter((x) => x.hideInUI !== true).map((x) => ({ label: `${x.symbol} (${x.isoCode})`, value: x.id })),
        { value: item?.currency }
      ),
  };

  // Check that each dynamic option is correct
  let usageOptionsOK = true;
  if (usageOptions && Object.keys(usageOptions).length > 0) {
    const filteredUsageOpt_CF = getUsageOptionsByFilteredCF(usageOptions, filteredCF_UsageOpt, item);
    // Check if all usage options are filled
    const usageOptionsToCheck = Object.keys(filteredUsageOpt_CF).length > 0 ? filteredUsageOpt_CF : usageOptions;
    usageOptionsOK = Object.keys(usageOptionsToCheck).every((ut) => item?.[ut]);
  }

  const usageDetails = state?.usageDataModal?.usageDetails;

  const combinedFactors = [...(filteredCF_UsageOpt || []), ...(conversionFactors || [])];

  const fetchedFactors = Array.from(new Set(combinedFactors.map((factor) => factor.id))).map((id) =>
    combinedFactors.find((factor) => factor.id === id)
  );

  const availableFactors = usageDetails?.location?.reportingYear?.conversionFactors
    ? usageDetails?.location?.reportingYear?.conversionFactors
    : usageDetails?.location?.calendarYear?.conversionFactors;

  const compareProperties = (item, usageDetails, usageOptions) => {
    // Compare entity, subentity, metric, dateStart and dateEnd
    if (
      item?.entity !== usageDetails?.entity ||
      item?.subentity !== usageDetails?.subentity ||
      item?.metric !== usageDetails?.metric ||
      item?.dateStart !== usageDetails?.dateStart ||
      item?.dateEnd !== usageDetails?.dateEnd
    ) {
      return false;
    }

    // Compare additional usage options if any
    if (usageOptions) {
      for (const usageType of Object.keys(usageOptions)) {
        if (item?.[usageType] !== usageDetails?.[usageType]) {
          return false;
        }
      }
    }
    return true;
  };

  let factors = availableFactors && compareProperties(item, usageDetails, usageOptions) ? availableFactors : fetchedFactors;

  const orgDetails = state?.organisation?.details;
  const factorYear = factors && getFactorYear(item, orgDetails);

  // Filter factors by selected factor provider and most recent year
  factors = factors?.filter((f) => {
    const factorProviderMatch =
      f?.factorprovider && item?.factorprovider ? f?.factorprovider === item?.factorprovider : true;
    const usageOptionsMatch =
      usageOptions &&
      Object.keys(usageOptions)?.every((usageType) => {
        return item?.[usageType] ? f[usageType] === item[usageType] : true;
      });
    const metricMatch = f.metric && item?.metric ? f.metric === item?.metric : true;

    return factorProviderMatch && usageOptionsMatch && metricMatch;
  });

  const closestYear =
    factors &&
    factors
      .map((f) => f.year)
      .filter((year) => year <= factorYear)
      .reduce((prev, curr) => (Math.abs(curr - factorYear) < Math.abs(prev - factorYear) ? curr : prev), -Infinity);

  factors = factors?.filter((f) => Number(f.year) === Number(closestYear));

  const seeDetails = new Map();
  seeDetails.set('Factor Year', (index) => factors?.[index]?.year);
  seeDetails.set('Source', (index) => factors?.[index]?.source);
  //Adding the usage options to the seeDetails map
  Object.keys(usageOptions || {}).forEach((key) => {
    seeDetails.set(_.find(usageTypeDetails, { type: key })?.displayName, (index) => {
      const option = _.find(usageOptions[key], { id: factors?.[index]?.[key] });
      const name = option?.name;
      if (!option?.hideInReporting) {
        return name;
      }
      return null; // Return null if the option is hidden in the Reporting
    });
  });
  seeDetails.set('Usage Sub Type', (index) => _.find(subentities, { id: factors?.[index]?.subentity })?.name);
  seeDetails.set('Metric', (index) => _.find(metrics, { id: factors?.[index]?.metric })?.name);

  const features = state?.features;
  const availableFeatures = state.organisation?.details?.features;
  const feature = features?.find((feature) => feature.featureKey === 'FACTOR_DETAILS');
  const hasAccess = availableFeatures?.includes('FACTOR_DETAILS') || feature?.enabledByDefault;
  const aprilFools = features?.find((feature) => feature.featureKey === 'APRIL_FOOLS');
  const hasAprilFools = availableFeatures?.includes('APRIL_FOOLS') || aprilFools?.enabledByDefault;

  return {
    item,
    metric,
    options,
    waterTreatment,
    solarConsumed,
    usageOptionsOK,
    currency,
    adminPortal,
    orderPriority,
    usageTypeDetails,
    solarSubentities,
    conversionFactors,
    organisationCurrency,
    // suppliersEmissionsFactor,
    // useSupplierCarbonFactor,
    hasAprilFools: hasAprilFools || false,
    aprilFools,
    user: state?.profile?.details?.userSub,
    showLoading: state.showLoader,
    gridSources: state?.gridSources,
    prevObj: state?.usageDataModal?.prevObj,
    prevDates: state?.usageDataModal?.prevDates,
    usageDetails,
    cyfDataAdmin: profile?.cyfAdmin,
    electricitySources: state?.admin?.adminPortal?.electricitySources,
    metrics,
    seeDetails,
    branding: state?.branding?.colors,
    font: state?.branding?.fonts[0]?.font?.body,
    factors,
    factorDetails: hasAccess || false,
    feature,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getUsageById: (id) => dispatch(USAGEACTIONS.getUsageById(id)),
  getAllItemsFromDB: (table) => dispatch(ADMINACTIONS.getAllItemsFromDB(table)),
  showUsageDataModal: (status, item) => dispatch(USAGEACTIONS.showUsageDataModal(status, item)),
  editUsageModal: (selectedInput, event) => dispatch(USAGEACTIONS.editUsageModal(selectedInput, event)),
  saveUsageData: (usageData, adminPortal) => dispatch(USAGEACTIONS.saveUsageData(usageData, adminPortal)),
  getFilteredFactors: (year, subentity, gm, metric) =>
    dispatch(USAGEACTIONS.getFilteredFactors(year, subentity, gm, metric)),
  getUsageOptions: (entity) => dispatch(USAGEACTIONS.getUsageOptions(entity)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddEditUsage);
